import {addRule, deleteRule, updateRule, updateRoleStatus} from "../api/rule";
import {ensureResponses} from "../model/robot";
import {Rule} from "../model/types";

type UseRuleMutationsProps = {
  userToken: string;
  rules: Rule[];
  editingRule: Rule | null;
  setEditingRule: (rule: Rule | null) => void;
  setSelectedId: (id: string) => void;
  setDialogContent: (content: string) => void;
  fetchRules: () => Promise<void>;
};

export const useRuleMutations = ({
  userToken,
  rules,
  editingRule,
  setEditingRule,
  setSelectedId,
  setDialogContent,
  fetchRules,
}: UseRuleMutationsProps) => {
  const removeRule = async (id: string) => {
    try {
      setDialogContent("loading");
      await deleteRule({token: userToken, id});
      await fetchRules();
    } catch (error) {
      console.log(error);
    } finally {
      setDialogContent("");
      setSelectedId("");
      setEditingRule(null);
    }
  };

  const toggleRule = async (id: string) => {
    try {
      setDialogContent("loading");
      const enabled = !rules.find((rule) => rule.id === id)?.enabled;
      await updateRoleStatus({token: userToken, id, enabled});
      await fetchRules();
    } catch (error) {
      console.log(error);
    } finally {
      setDialogContent("");
    }
  };

  const selectRule = (id: string) => {
    const selectedRule = rules.find((rule) => rule.id === id);
    setSelectedId(id);
    setEditingRule(selectedRule || null);
  };

  const saveRule = async () => {
    if (!editingRule) return;
    const reducedResponses = ensureResponses(editingRule.responses);

    try {
      setDialogContent("loading");
      if (editingRule.id) {
        await updateRule({
          token: userToken,
          rule: {
            ...editingRule,
            responses: reducedResponses,
          },
        });
      } else {
        await addRule({
          token: userToken,
          rule: {
            name: editingRule.name,
            priority: editingRule.priority,
            enabled: editingRule.enabled,
            match: editingRule.match,
            responses: editingRule.responses,
          },
        });
      }
      await fetchRules();
      setSelectedId("");
      setEditingRule(null);
    } catch (error) {
      console.log(error);
    } finally {
      setDialogContent("");
    }
  };

  return {
    removeRule,
    toggleRule,
    selectRule,
    saveRule,
  };
};
